import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AddGroupDialogComponent } from '../add-group-dialog/add-group-dialog.component';
import { AddCapacityDialogComponent } from '../add-capacity-dialog/add-capacity-dialog.component';
import { EditGroupDialogComponent } from '../edit-group-dialog/edit-group-dialog.component';
import { EditCapacityDialogComponent } from '../edit-capacity-dialog/edit-capacity-dialog.component';
import { PlusButtonDialogComponent } from '../add-app-dialog/add-app-dialog.component';
import { DeleteDialogComponent } from '../../../../design-system/delete-dialog/delete-dialog.component';
import { ApplicationInstance } from '../../../../../services/model/new-application.model';
import { FunctionalCapacityGroupDiagram } from '../../../../../services/back/functional-capacities.service';

export interface DialogConfig {
	width?: string;
	maxHeight?: string;
	disableClose?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class DialogManagerService {
	private readonly defaultConfig: DialogConfig = {
		width: '475px',
		maxHeight: '80vh',
		disableClose: false
	};

	constructor(
		private dialog: MatDialog,
		private translate: TranslateService
	) {}

	openAddGroupDialog(tenantId: string): Observable<any> {
		const dialogRef = this.dialog.open(AddGroupDialogComponent, {
			data: { tenantId },
			...this.defaultConfig
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result?.result)
		);
	}

	openAddCapacityDialog(
		capacityId: string,
		groupName: string,
		tenantId: string,
		tenantFunctionalCapacitiesGroups: FunctionalCapacityGroupDiagram[]
	): Observable<any> {
		const dialogRef = this.dialog.open(AddCapacityDialogComponent, {
			data: {
				id: capacityId,
				group: groupName,
				tenantId,
				tenantFunctionalCapacitiesGroups
			},
			width: '475px',
			maxHeight: '380px',
			disableClose: false
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result?.result)
		);
	}

	openEditGroupDialog(
		capacityId: string,
		name: string,
		icon: string
	): Observable<any> {
		const dialogRef = this.dialog.open(EditGroupDialogComponent, {
			data: {
				id: capacityId,
				name,
				icon
			},
			...this.defaultConfig
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result?.result)
		);
	}

	openEditCapacityDialog(
		capacityId: string,
		name: string
	): Observable<any> {
		const dialogRef = this.dialog.open(EditCapacityDialogComponent, {
			data: {
				id: capacityId,
				name
			},
			width: '475px',
			maxHeight: '380px',
			disableClose: false
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result?.result)
		);
	}

	openPlusButtonDialog(
		buttonId: string,
		applications: ApplicationInstance[],
		applicationSelected: any[]
	): Observable<any> {
		const dialogRef = this.dialog.open(PlusButtonDialogComponent, {
			data: {
				id: buttonId,
				applications,
				applicationSelected
			},
			width: '475px',
			maxHeight: '85vh',
			disableClose: false
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result?.result)
		);
	}

	openDeleteGroupDialog(): Observable<boolean> {
		const dialogRef = this.dialog.open(DeleteDialogComponent, {
			width: '475px',
			data: {
				icon: 'assets/illustrations/delete-functional-capacity.svg',
				title: this.translate.instant('page.diagram.capacity.delete-group.title'),
				subtitle: this.translate.instant('page.diagram.capacity.delete-group.subtitle'),
				warningMessage: this.translate.instant('page.diagram.capacity.delete-group.warningMessage'),
				deleteButton: this.translate.instant('page.diagram.capacity.delete-group.deleteButton')
			}
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result)
		);
	}

	openDeleteCapacityDialog(): Observable<boolean> {
		const dialogRef = this.dialog.open(DeleteDialogComponent, {
			width: '475px',
			data: {
				icon: 'assets/illustrations/delete-functional-capacity.svg',
				title: this.translate.instant('page.diagram.capacity.delete-capacity.title'),
				subtitle: this.translate.instant('page.diagram.capacity.delete-capacity.subtitle'),
				warningMessage: this.translate.instant('page.diagram.capacity.delete-capacity.warningMessage'),
				deleteButton: this.translate.instant('page.diagram.capacity.delete-capacity.deleteButton')
			}
		});

		return dialogRef.afterClosed().pipe(
			filter(result => result)
		);
	}

	// Méthodes utilitaires pour les configurations communes
	private getStandardConfig(overrides: Partial<DialogConfig> = {}): DialogConfig {
		return {
			...this.defaultConfig,
			...overrides
		};
	}

	private getSmallConfig(): DialogConfig {
		return this.getStandardConfig({
			width: '400px',
			maxHeight: '60vh'
		});
	}

	private getLargeConfig(): DialogConfig {
		return this.getStandardConfig({
			width: '600px',
			maxHeight: '90vh'
		});
	}
}
